// DOM overlay HUD, DayZ-minimal: near-invisible reticle, weapon/ammo readout,
// toast queue, F1 help sheet. Dev telemetry (state panel, online counter)
// lives behind the F3 debug toggle — the default HUD shows nothing else.
import { injectTheme } from './theme';
import { Toasts } from './toast';
import { initHudIdle, pokeHud } from './idle';
import { noiseMeter, healthColor, healthVisible, healthText } from './hudmodel.mjs';

const HELP = `
<b>ASHVALE — MOVEMENT & COMBAT TEST</b><br><br>
<table>
<tr><td>Mouse</td><td>turn — the camera rides your back</td><td>WASD</td><td>move (relative to view)</td></tr>
<tr><td>Shift</td><td>sprint (forward)</td><td>Ctrl</td><td>sneak — slow &amp; silent</td></tr>
<tr><td>Alt (hold)</td><td>free-look — body locked, mouse looks</td><td>C / X</td><td>crouch / prone</td></tr>
<tr><td>F</td><td>pick up / interact (else gun-melee)</td><td></td><td></td></tr>
<tr><td>Space</td><td>jump / rise</td><td>Q / E</td><td>lean</td></tr>
<tr><td>V, 2×A/D</td><td>roll / dodge</td><td>K</td><td>die / respawn</td></tr>
<tr><td>1-7 / wheel</td><td>weapon</td><td>H</td><td>holster</td></tr>
<tr><td>M</td><td>map (world mode)</td><td>F6 / F3</td><td>sky mood / perf HUD</td></tr>
<tr><td>F4</td><td>animation log (rolling)</td><td>F2</td><td>animation debug panel</td></tr>
</table><br>
<b>SURVIVAL</b><br>
<table>
<tr><td>Tab</td><td>inventory (drag items, right-click for actions)</td><td></td><td></td></tr>
<tr><td>F / G</td><td>pick up / gather when prompted</td><td>8 9</td><td>quickslots (0 / - need better clothing)</td></tr>
</table>
Watch health / hunger / thirst — the glyph column in the BOTTOM RIGHT, which
only shows a vital once it is off-nominal, with your health in numbers beside
it. The five-segment bar in the bottom left is how far away you can be HEARD
right now, in metres: crouch (C) and walk instead of sprinting and it collapses
— floorboards, asphalt and metal roughly double it. Search buildings — canned food,
drinks, medicine, clothing and backpacks lie where people left them (schools,
hospitals and military sites hold the best finds). Forage mushrooms on forest
floors and berries on bushes — know what you eat: some species punish you,
one of them kills. Hunt chickens, goats and sheep in the open country and
butcher them with G. Right-click matches to light a campfire (or a kitchen
stove indoors), then right-click raw meat or toxic-raw forage next to the
flames to roast it safe. Rivers and lakes are drinkable with G at the
water's edge — raw lake water can make you sick, so find purification
tablets (pharmacies, hospitals, military sites) and carry them; drinking
uses one automatically. The sea is salt — don't bother.<br><br>
<b>MULTIPLAYER & PVP</b> — the world is shared: what anyone picks, loots,
drops, lights or hunts happens for everyone, and your character (gear,
vitals, position) is saved on the server between sessions. Other survivors
appear with nameplates and their weapons drawn — and yes, weapons hurt:
friendly fire is REAL, so aim carefully around family.<br><br>
<b>DANGER & BLEEDING</b> — wolf packs prowl the forests and the high
country, and sometimes they decide you look like dinner. Bites and weapon
hits can open wounds: watch the blood glyph (bottom right) and the flashing
BLEEDING warning, and patch yourself with a <b>bandage</b> or <b>duct
tape</b> (right-click → Patch wound, or a quickslot) before you bleed out.
Blood regrows slowly — only on a full stomach.<br><br>
<b>WEAPONS</b><br>
<table>
<tr><td>1</td><td>Fists</td><td>2</td><td>Knife</td></tr>
<tr><td>3</td><td>Baseball Bat</td><td>4</td><td>Fire Axe</td></tr>
<tr><td>5</td><td>Spear</td><td>6</td><td>Pistol</td></tr>
<tr><td>7</td><td>Rifle</td></tr>
</table><br>
<b>MELEE (LMB + WASD modifiers, chain = click again &lt;450ms)</b><br>
<table>
<tr><td>click</td><td>basic strike</td><td>click-click</td><td>chain move</td></tr>
<tr><td>3×click</td><td>big combo</td><td>W+click</td><td>power strike</td></tr>
<tr><td>A/D+click</td><td>left/right swing</td><td>S+click</td><td>heavy/uppercut</td></tr>
<tr><td>S+A / S+D+click</td><td>kicks</td><td>Shift+click</td><td>sprint attack</td></tr>
<tr><td>Space+click</td><td>jump attack</td><td>RMB hold</td><td>block</td></tr>
<tr><td>RMB+click</td><td>counter / shove</td><td>prone</td><td>knife stab only</td></tr>
</table><br>
<b>GUNS (6 pistol / 7 rifle)</b><br>
<table>
<tr><td>RMB</td><td>aim (ADS)</td><td>LMB</td><td>fire (rifle = full-auto)</td></tr>
<tr><td>R</td><td>reload (works prone)</td><td>F</td><td>melee: whip / rifle butt</td></tr>
</table><br>
<i>F1 toggles this sheet. F2 opens the animation debug panel (click clips to
play them; freeze pauses the controller). Hit the dummies. All melee/guns work
standing, walking, running, crouched and prone (prone = knife/guns).</i>`;

export class Hud {
  constructor() {
    injectTheme();
    const css = document.createElement('style');
    css.textContent = `
      .hud { position:fixed; font:400 var(--ui-s)/1.5 var(--ui-font); letter-spacing:var(--ui-track);
             color:var(--hud-text); z-index:10; text-shadow:var(--hud-shadow); }
      /* --- F3 debug layer: monospace telemetry, hidden by default --- */
      .hud-debug { display:none; font:12px/1.5 monospace; letter-spacing:0; color:#cfd4c8;
                   background:rgba(10,10,10,.72); border:1px solid #2c3542; border-radius:4px;
                   padding:8px 12px; text-shadow:none; }
      .hud-debug.on { display:block; }
      #hud-state { left:var(--hud-margin); top:var(--hud-margin); min-width:210px; }
      #hud-state b { color:#e8d9a0; }
      #hud-online { left:var(--hud-margin); top:150px; font-size:12px; padding:4px 12px; }
      #hud-online .dot { display:inline-block; width:7px; height:7px; border-radius:50%;
                         background:#7a9c5c; margin-right:6px; }
      #hud-online.off .dot { background:#c0392b; }
      /* --- gameplay HUD --- */
      /* Weapon readout. DayZ has no ammo counter at all — you check the
         magazine — so ours has to earn its place by being the quietest thing
         that can still answer "can I fire". Round 5 set a 23px 600-weight
         near-white "30/30" in the corner of every frame: on the sunset town
         street it was the third-brightest cluster in the image. It is now
         type-scaled down one step, carried by weight and tracking instead of
         size, and it settles with the rest of the HUD (see idle.js). The
         magazine count is the one part that stays bright while awake — it is
         the data; the weapon name is chrome.
         THE SHADOW IS NOT DECORATION, IT IS THE CONTRAST GUARANTEE. Round 6
         attenuated the settled readout with opacity:.26 on the parent, and
         parent opacity composites the glyph AND its text-shadow together: the
         one thing holding the type off an arbitrary world faded at exactly the
         same rate as the type. On round-006/third_person_weapon.png (bottom
         strip lum 88) "RIFLE 30/30" was a grey ghost. The shadow is now held at
         FULL strength in every state and only the fill alpha moves, with a hard
         floor under the magazine count — the one glyph that has to survive. */
      #hud-weapon { left:var(--hud-margin); bottom:var(--hud-margin); text-align:left;
                    text-shadow:0 1px 3px rgba(0,0,0,.92), 0 0 3px rgba(0,0,0,.7),
                                0 0 9px rgba(0,0,0,.45); }
      #hud-weapon .wname { font-size:calc(11px * var(--ui-scale)); font-weight:500;
                           color:var(--hud-dim); text-transform:uppercase; letter-spacing:.18em; }
      #hud-weapon .ammo { font-size:var(--ui-m); font-weight:600; color:var(--hud-text);
                          line-height:1.25; font-variant-numeric:tabular-nums;
                          letter-spacing:.04em; }
      /* wake snaps (no transition at all), settle drifts — the transition is
         declared on the hud-idle rules below. See the quickbar note in
         inventory.js for why the wake direction must not interpolate. */
      #hud-weapon .ammo b { font-weight:600; color:var(--ui-hi); }
      #hud-weapon .ammo i { font-style:normal; color:var(--hud-dim); font-weight:400; }
      /* Settled state: the readout recedes, it does not dissolve. What recedes
         is the CHROME — the weapon name drops to .46 and the magazine capacity
         to .55 — while the magazine count itself, the only glyph that answers
         "can I fire", holds a floor of .86 over an undimmed shadow. Measured on
         the sunset town street (bottom strip lum ~96, the brightest background
         in the capture set): at .72 the digits still read as a smudge at 1:1;
         at .86 they read as digits, and they are still a 16 px 600-weight pair
         in a corner rather than round 5's 23 px near-white block. */
      body.hud-idle #hud-weapon .wname,
      body.hud-idle #hud-weapon .ammo b,
      body.hud-idle #hud-weapon .ammo i { transition:color .7s ease-out; }
      body.hud-idle #hud-weapon .wname { color:rgba(154,160,166,.46); }
      body.hud-idle #hud-weapon .ammo b { color:rgba(244,244,241,.86); }
      body.hud-idle #hud-weapon .ammo i { color:rgba(154,160,166,.55); }
      #hud-weapon .sweep { width:12px; height:12px; margin-left:6px; vertical-align:-1px;
                           animation:hudspin .9s linear infinite; }
      @keyframes hudspin { to { transform:rotate(360deg); } }
      #hud-move { left:50%; top:26%; transform:translateX(-50%); font-size:var(--ui-m);
                  color:var(--hud-text); opacity:0; transition:opacity .15s; pointer-events:none; }
      #hud-help { right:var(--hud-margin); top:var(--hud-margin); max-width:480px; display:none;
                  max-height:90vh; overflow-y:auto; font-size:var(--ui-s); text-shadow:none;
                  background:rgba(12,12,12,.88); border:1px solid var(--hud-line);
                  border-radius:var(--hud-radius); padding:14px 18px; }
      #hud-help table { border-spacing:6px 1px; }
      #hud-help td:nth-child(odd) { color:#d8c86a; white-space:nowrap; }
      #hud-help b { letter-spacing:.08em; }
      #hud-hint { left:50%; bottom:76px; transform:translateX(-50%); font-size:var(--ui-s);
                  color:var(--hud-text); }
      /* --- reticle: 2px white core, 1px HARD black outline, 3px dark halo.
         The halo is what makes it survive sunlit grass — a .6 alpha 1px ring
         disappeared entirely in refs/ours/round-003/ads_rifle.png. --- */
      #crosshair { position:fixed; left:50%; top:50%;
                   width:calc(3px * var(--ui-scale)); height:calc(3px * var(--ui-scale));
                   margin:calc(-1.5px * var(--ui-scale));
                   background:#fff; border-radius:50%;
                   box-shadow:0 0 0 1px rgba(0,0,0,.95), 0 0 0 4px rgba(0,0,0,.55);
                   opacity:.78; z-index:9; transition:opacity .12s; pointer-events:none; }
      /* DayZ draws no hip reticle at all. Ours has to stay (the shot goes where
         the dot is), but it settles with the rest of the HUD — and the click
         that fires the weapon is itself the wake event, so it is always at full
         strength in the moment it matters. */
      body.hud-idle #crosshair { opacity:.3; transition:opacity .7s ease-out; }
      /* ADS. Round 5 painted a pure-#fff 4-tick bracket at .95 opacity plus a
         Call-of-Duty radial vignette, both anchored to viewport centre. In
         refs/ours/round-005/ads_rifle.png the bracket is the brightest pixel
         cluster in a frame whose bottom region peaks at 121 — a UI element
         out-shouting a muzzle flash. Round 6 answered by deleting the aim
         state outright, and refs/ours/round-006/ads_rifle.png is the result: a
         frame in which NOTHING says the player is aiming. Shipping neither is
         worse than shipping either.
         What ships now is the quiet half of the round-5 sight picture and none
         of the loud half: ONE soft vignette that never exceeds .22 black, no
         brackets, no ticks, no white anywhere. It is a lens/eye-relief cue —
         the periphery falls away when you bring the weapon up — not a HUD
         element, and it cannot out-shout a muzzle flash because it contains no
         light at all.
         The centre mark stays and is not a fiction: controller.js:190 traces
         the shot from the camera along camera.forward(), i.e. exactly screen
         centre, so a mark there is the truth about where the round goes even
         while the weapon MODEL still renders off-axis (that alignment lives in
         weapons.js + camera.js + grip-config.json and is not in this round's
         file ownership — see notes). What it must not do is settle: a player
         holding the aim button is the opposite of idle, so ADS is exempted
         from the idle attenuation that was hiding it at .3. */
      #crosshair.ads { opacity:.5; width:calc(2px * var(--ui-scale));
                       height:calc(2px * var(--ui-scale)); margin:calc(-1px * var(--ui-scale));
                       background:var(--hud-bone);
                       box-shadow:0 0 0 1px rgba(0,0,0,.95), 0 0 0 4px rgba(0,0,0,.4); }
      /* not a duplicate: body.hud-idle #crosshair outranks #crosshair.ads, which
         is why the aim mark was rendering at .3 in round-006/ads_rifle.png */
      body.hud-idle #crosshair.ads { opacity:.5; }
      #ads-vig { position:fixed; inset:0; z-index:8; pointer-events:none; opacity:0;
                 transition:opacity .18s ease-out;
                 background:radial-gradient(ellipse 60% 56% at 50% 50%,
                   rgba(0,0,0,0) 54%, rgba(0,0,0,.10) 76%, rgba(0,0,0,.22) 100%); }
      #ads-vig.on { opacity:1; }
      /* hit feedback: four 1px ticks expanding 3px outward over 90ms, white, .5 opacity */
      #hitmarks { position:fixed; left:50%; top:50%; width:0; height:0; z-index:9;
                  pointer-events:none; display:none; }
      #hitmarks.go { display:block; }
      #hitmarks span { position:absolute; background:#fff; opacity:0; }
      #hitmarks .n, #hitmarks .s { width:1px; height:4px; left:0; }
      #hitmarks .e, #hitmarks .w { width:4px; height:1px; top:0; }
      #hitmarks.go .n { animation:tickn 90ms ease-out; }
      #hitmarks.go .s { animation:ticks 90ms ease-out; }
      #hitmarks.go .e { animation:ticke 90ms ease-out; }
      #hitmarks.go .w { animation:tickw 90ms ease-out; }
      @keyframes tickn { from { opacity:.5; top:-7px; } to { opacity:0; top:-10px; } }
      @keyframes ticks { from { opacity:.5; top:3px; } to { opacity:0; top:6px; } }
      @keyframes ticke { from { opacity:.5; left:3px; } to { opacity:0; left:6px; } }
      @keyframes tickw { from { opacity:.5; left:-7px; } to { opacity:0; left:-10px; } }
      /* --- wound infection (docs/INFECTED.md Z54, Z125–Z131) ---------------
         Same plate the vitals column uses (34px, value bar, blink only when
         critical), parked one slot to the LEFT of that column so it reads as
         part of the same cluster and never fights it for height — the column
         grows upward when a bandage slot appears. Hidden unless there is
         something to say. */
      #hud-infection { position:fixed; right:calc(var(--hud-margin) + 39px * var(--ui-scale));
                       bottom:var(--hud-margin); z-index:10; display:none; }
      #hud-infection.on { display:block; }
      #hud-infection .islot { position:relative; box-sizing:border-box;
                width:calc(34px * var(--ui-scale)); height:calc(34px * var(--ui-scale));
                background:rgba(0,0,0,.44); border:1px solid rgba(255,255,255,.09);
                border-radius:var(--hud-radius); display:flex; align-items:center;
                justify-content:center; box-shadow:0 1px 4px rgba(0,0,0,.55); }
      #hud-infection .iic { width:calc(21px * var(--ui-scale)); height:calc(21px * var(--ui-scale));
                filter:drop-shadow(0 1px 1px rgba(0,0,0,.9)); }
      #hud-infection .iic.fever { animation:ifever 2.6s ease-in-out infinite; }
      #hud-infection .iic.crit { animation:iblink .9s infinite; }
      #hud-infection .iv { position:absolute; left:1px; bottom:1px; height:2px; display:block; }
      @keyframes ifever { 0%,100% { opacity:1; } 50% { opacity:.55; } }
      @keyframes iblink { 0%,100% { opacity:1; } 50% { opacity:.2; } }
      /* the fever itself, not a readout: a warm haze that breathes at the
         edges of vision once the shakes start. Never above .16 alpha. */
      #fever-vig { position:fixed; inset:0; z-index:7; pointer-events:none; opacity:0;
                   transition:opacity 1.2s ease-out; will-change:transform;
                   background:radial-gradient(ellipse 74% 70% at 50% 50%, rgba(0,0,0,0) 45%,
                     rgba(120,26,14,.30) 78%, rgba(96,16,8,.62) 100%); }
      /* the breath is a composited transform, and only runs while it shows */
      #fever-vig.on { animation:ibreathe 5.5s ease-in-out infinite; }
      @keyframes ibreathe { 0%,100% { transform:scale(1); } 50% { transform:scale(1.05); } }
      /* The stage, in words, because A NATIVE title= CAN NEVER FIRE IN THIS
         GAME: the canvas holds pointer lock whenever the player is alive and
         looking around, so the plate cannot be hovered and the Fever / Sick /
         Sepsis distinction — three different illnesses with three different
         urgencies — was literally unreachable.
         It is not a permanent caption either: the plate's own comment says
         "nothing is spelled out on screen", and a word parked in the corner
         for the fifteen minutes an infection runs would break that. So it
         rides the EXISTING idle rule (idle.js): any key, click or wheel notch
         brings it back, and 4.5 s later it settles away again and leaves the
         glyph. That also keeps it out of the beauty shots, which are captured
         in the settled state. */
      #hud-infection .ilabel { position:absolute; right:0; bottom:calc(100% + 5px);
                white-space:nowrap; font:500 calc(10px * var(--ui-scale))/1.3 var(--ui-font);
                letter-spacing:.14em; text-transform:uppercase; color:var(--hud-dim);
                background:rgba(9,9,9,.84); border:1px solid var(--panel-line);
                padding:2px 6px; text-shadow:none; opacity:.9; }
      body.hud-idle #hud-infection .ilabel { opacity:0; transition:opacity .7s ease-out; }
      /* --- damage feedback (model: src/game/hurt.mjs) ---------------------
         Same construction as #fever-vig above, for the same reason: a
         full-screen gradient layer whose only moving part is its own opacity,
         so a hit costs one composited property and no layout.
         THE HUD RULE — NOTHING MAY BECOME THE BRIGHTEST CLUSTER IN THE FRAME —
         holds here by construction, not by taste: the peak stop is #6a0c06,
         luma 31/255, and hurt.mjs caps the layer at VIG_MAX = .72 alpha. A
         near-black red at .62 effective alpha can only ever DARKEN what is
         under it; on the brightest plate in the capture set (bottom strip
         ~121) it pulls the periphery down toward 50. It is the opposite of
         round 5's white ADS bracket.
         It is also deliberately stronger than the fever haze's .16 cap: the
         fever is a fifteen-minute state and this is under a second. */
      /* No will-change: that promotes a full-screen compositing layer for the
         whole session (~8 MB of VRAM at 1080p, each) for something that is
         invisible most of it, and this project has an explicit memory risk on
         the weakest target. The opacity transition promotes the layer for the
         half-second it is actually animating. */
      #hurt-vig { position:fixed; inset:0; z-index:8; pointer-events:none; opacity:0;
                  background:radial-gradient(ellipse 74% 70% at 50% 50%, rgba(0,0,0,0) 36%,
                    rgba(106,12,6,.34) 72%, rgba(106,12,6,.86) 100%); }
      /* The directional arc. NOT a full-screen rotated layer: a single blurred
         blob parked on a circle around screen centre and rotated tangentially,
         positioned from JS because CSS cannot take a sine. That needs no
         conic-gradient, no mask-image and no element larger than the viewport,
         and it reads the way every damage-direction indicator reads.
         It only appears when the hit path actually knew a bearing — see the
         survey in hurt.mjs: today that is the infected (local sim and server),
         and nothing else. An invented arrow would be worse than none. */
      #hurt-arc { position:fixed; left:50%; top:50%; width:0; height:0; z-index:8;
                  pointer-events:none; opacity:0; }
      #hurt-arc i { position:absolute; display:block;
                  width:calc(200px * var(--ui-scale)); height:calc(46px * var(--ui-scale));
                  margin-left:calc(-100px * var(--ui-scale)); margin-top:calc(-23px * var(--ui-scale));
                  border-radius:50%; filter:blur(2px);
                  background:radial-gradient(ellipse at 50% 50%, rgba(170,22,12,.92) 0%,
                    rgba(126,14,8,.42) 56%, rgba(126,14,8,0) 100%); }
      /* --- numeric health (agenda WS14.16) ------------------------------
         The only in-play health signal was four colour buckets on a 24 px
         glyph: 0.24 px of information per hit point, and no way to tell 70
         from 40. The number sits in the SAME bottom-right cluster as that
         glyph, one slot to its left, and steps one slot further left when the
         infection plate is up (both elements are owned here, so the two can
         never collide).
         Quiet by absence: below 95 only — the same threshold that brings the
         heart glyph into the vitals column — so a healthy survivor's frame is
         unchanged and the beauty shots never see it. The fill rides the
         vitals ramp (bone → gold → amber → brick), never --ui-hi, and the
         shadow is held at full strength in every state (the round-6 lesson in
         the weapon readout above: parent opacity composites the glyph and the
         shadow that guarantees its contrast together, so only the FILL may
         attenuate). */
      #hud-health { position:fixed; right:calc(var(--hud-margin) + 39px * var(--ui-scale));
                    bottom:var(--hud-margin); z-index:10; display:none; line-height:1;
                    text-align:right; white-space:nowrap;
                    text-shadow:0 1px 3px rgba(0,0,0,.92), 0 0 3px rgba(0,0,0,.7),
                                0 0 9px rgba(0,0,0,.45); }
      #hud-health.on { display:block; }
      #hud-health.shift { right:calc(var(--hud-margin) + 78px * var(--ui-scale)); }
      #hud-health .hl { font-size:calc(10px * var(--ui-scale)); font-weight:500;
                    color:var(--hud-dim); letter-spacing:.16em; margin-right:4px; }
      #hud-health .hv { font-size:var(--ui-m); font-weight:600; letter-spacing:.03em;
                    font-variant-numeric:tabular-nums; }
      /* Settled: the CHROME recedes, the DATA does not — the same split the
         weapon readout makes two screens up. "HP" dims; the number itself is
         the one glyph that answers "am I about to die", so it holds its fill
         over an undimmed shadow. */
      body.hud-idle #hud-health .hl { color:rgba(154,160,166,.46);
                    transition:color .7s ease-out; }
      /* --- noise meter (model: src/ui/hudmodel.mjs, maths: game/noise.js) --
         world.noiseState() has returned { n, v } every frame since the
         infected shipped and NOTHING read it, while stance, speed and surface
         move the player's audible radius by a factor of seven — the single
         largest hidden mechanic in the game. Five segments, 3 px tall, plus
         the radius in metres, because the model's own unit is metres.
         Bottom-left column, between the weapon readout (0–37 px) and the
         toast lane (62 px up), all three offsets now scaled together.
         ABSENT WHILE SILENT: a radius of 0 — standing still — removes the
         meter entirely rather than showing an empty bar. */
      #hud-noise { position:fixed; left:var(--hud-margin);
                   bottom:calc(var(--hud-margin) + 42px * var(--ui-scale));
                   z-index:10; display:none; align-items:center;
                   gap:calc(7px * var(--ui-scale)); }
      #hud-noise.on { display:flex; }
      #hud-noise .nsegs { display:flex; gap:2px; }
      #hud-noise .nsg { width:calc(9px * var(--ui-scale)); height:3px;
                   background:rgba(255,255,255,.11); box-shadow:0 1px 2px rgba(0,0,0,.8); }
      #hud-noise .nsg.lit { background:var(--hud-bone); }
      #hud-noise.heard .nsg.lit { background:var(--hud-warn); }
      #hud-noise.loud .nsg.lit { background:var(--hud-bad); }
      #hud-noise .nm { font:500 calc(10px * var(--ui-scale))/1 var(--ui-font);
                   letter-spacing:.1em; color:var(--hud-dim);
                   font-variant-numeric:tabular-nums; text-shadow:var(--hud-shadow); }
      /* holding W auto-repeats keydown, so the meter is awake whenever it has
         anything to say; the settled rule is for the frame after you stop */
      body.hud-idle #hud-noise { opacity:.5; transition:opacity .7s ease-out; }
      body.hud-idle #hud-noise.loud { opacity:.8; }
    `;
    document.head.appendChild(css);
    this.toasts = new Toasts();
    this.state = this._el('hud-state', 'hud hud-debug');
    this.weaponEl = this._el('hud-weapon', 'hud');
    this.move = this._el('hud-move', 'hud');
    this.help = this._el('hud-help', 'hud');
    this.help.innerHTML = HELP;
    this.hint = this._el('hud-hint', 'hud');
    this.online = this._el('hud-online', 'hud hud-debug');
    this.hint.textContent = 'Click to capture mouse — F1 for controls';
    this.crosshair = document.createElement('div');
    this.crosshair.id = 'crosshair';
    document.body.appendChild(this.crosshair);
    this.adsVig = document.createElement('div');
    this.adsVig.id = 'ads-vig';
    document.body.appendChild(this.adsVig);
    this.infEl = this._el('hud-infection', '');
    this.feverVig = document.createElement('div');
    this.feverVig.id = 'fever-vig';
    document.body.appendChild(this.feverVig);
    this._infKey = null; // last rendered state, so this costs nothing per frame
    // damage feedback. Appended AFTER #fever-vig and #ads-vig so a hit reads
    // on top of the fever haze at the same z-index instead of under it.
    this.hurtVig = document.createElement('div');
    this.hurtVig.id = 'hurt-vig';
    document.body.appendChild(this.hurtVig);
    this.hurtArc = document.createElement('div');
    this.hurtArc.id = 'hurt-arc';
    this.hurtArc.innerHTML = '<i></i>';
    document.body.appendChild(this.hurtArc);
    this._hurtA = 0;      // peak alpha of the flash currently decaying
    this._hurtT = 0;      // when that peak was set
    this._hurtMs = 0;     // how long it has to decay
    this._hurtTimer = null;
    this.healthEl = this._el('hud-health', '');
    this._hpKey = null;
    this.noiseEl = this._el('hud-noise', '');
    this._noiseKey = null;
    this._noiseFed = false;   // has anything called setNoise() yet?
    this._noiseSegs = null;
    this._noiseNum = null;
    this._bridgeNoise();
    this.hitEl = document.createElement('div');
    this.hitEl.id = 'hitmarks';
    this.hitEl.innerHTML = '<span class="n"></span><span class="s"></span><span class="e"></span><span class="w"></span>';
    document.body.appendChild(this.hitEl);
    this._debug = false;
    this._online = undefined; // undefined = never reported
    this._wKey = undefined;
    initHudIdle();
    this._moveTimer = null;
    this._hitTimer = null;
    document.addEventListener('pointerlockchange', () => {
      this.hint.style.display = document.pointerLockElement ? 'none' : 'block';
    });
    // telemetry rides the same F3 key as the perf HUD (its own listener)
    window.addEventListener('keydown', (e) => {
      if (e.code === 'F3') {
        this._debug = !this._debug;
        this.state.classList.toggle('on', this._debug);
        this.online.classList.toggle('on', this._debug && this._online !== undefined);
      }
    });
  }

  _el(id, cls) {
    const el = document.createElement('div');
    el.id = id; el.className = cls;
    document.body.appendChild(el);
    return el;
  }

  // n = other survivors online; null = server unreachable (F3 debug layer)
  setOnline(n) {
    this._online = n;
    this.online.className = 'hud hud-debug' + (n == null ? ' off' : '') + (this._debug ? ' on' : '');
    this.online.innerHTML = n == null
      ? '<span class="dot"></span>offline'
      : `<span class="dot"></span>${n} other${n === 1 ? '' : 's'} online`;
  }

  // ---- damage feedback ----------------------------------------------------
  // `ev` is one event out of src/game/hurt.mjs's HurtFeed — it has already
  // decided severity, direction, alpha, decay and whether a sound is owed, so
  // this method only paints. Called from stats.damage(), which is the single
  // place every source of harm in the game funnels through.
  hurt(ev) {
    if (!ev) return;
    // A second hit may only DEEPEN a flash that is still decaying, never cut
    // it short — otherwise the third round of a burst reads as weaker than
    // the first, because each new attack restarts from zero mid-decay.
    // What is still on screen is estimated from the previous peak and how far
    // through its decay we are, NOT from the peak alone: taking the old peak
    // verbatim would make a graze half a second after a rifle round re-flash
    // at the rifle's strength.
    const now = performance.now();
    const spent = this._hurtMs > 0 ? (now - this._hurtT) / this._hurtMs : 1;
    const residual = (this._hurtA || 0) * Math.max(0, 1 - spent);
    const a = Math.max(ev.alpha, residual);
    this._hurtA = a;
    this._hurtT = now;
    this._hurtMs = ev.ms;
    // Commit the attack with no transition, then install the decay. The forced
    // reflow is what separates the two style recalcs — same trick as
    // hitmarker() below, and the reason it is needed is identical.
    //
    // BOTH layers are committed before that reflow and both decays after it,
    // so a hit costs ONE synchronous layout, not two. A forced layout flushes
    // the whole document, not the element it was read from, so the arc does not
    // need its own; with the arc now actually firing (main.js hands the
    // infected hit its bearing) a five-infected mob was otherwise ~10 forced
    // reflows a second on a page carrying the entire DOM HUD.
    const arc = Number.isFinite(ev.ang);
    this.hurtVig.style.transition = 'none';
    this.hurtVig.style.opacity = a;
    if (arc) {
      // park the blob on a circle at 36 % of the shorter viewport edge and
      // rotate it tangentially; 0 rad is dead ahead (top of screen)
      const r = 0.36 * Math.min(window.innerWidth || 1600, window.innerHeight || 900);
      const x = Math.sin(ev.ang) * r, y = -Math.cos(ev.ang) * r;
      this.hurtArc.firstChild.style.transform =
        `translate(${x.toFixed(1)}px,${y.toFixed(1)}px) rotate(${ev.deg}deg)`;
      this.hurtArc.style.transition = 'none';
      this.hurtArc.style.opacity = Math.min(0.95, 0.45 + 0.5 * ev.sev);
    }
    void this.hurtVig.offsetWidth;
    this.hurtVig.style.transition = `opacity ${ev.ms}ms ease-out`;
    this.hurtVig.style.opacity = 0;
    if (arc) {
      this.hurtArc.style.transition = `opacity ${Math.round(ev.ms * 0.8)}ms ease-out`;
      this.hurtArc.style.opacity = 0;
    }
    clearTimeout(this._hurtTimer);
    this._hurtTimer = setTimeout(() => { this._hurtA = 0; }, ev.ms + 60);
    // Being shot at is news: bring the HUD back so the player can read the
    // health number they are about to need. A pooled trickle pulse must NOT
    // wake it, or starving would hold the whole HUD awake forever.
    if (!ev.trickle && ev.sev >= 0.25) pokeHud();
  }

  // ---- numeric health ----------------------------------------------------
  // Driven from setInfection(), which already receives `stats` at 4 Hz, so
  // this needs no new call site in main.js. An integrator may call it
  // directly instead.
  setHealth(stats) {
    const v = stats?.health;
    const on = healthVisible(v);
    const key = on ? Math.round(v) : 'off';
    if (key === this._hpKey) return;
    this._hpKey = key;
    this.healthEl.classList.toggle('on', on);
    if (!on) { this.healthEl.innerHTML = ''; return; }
    this.healthEl.style.color = healthColor(v);
    this.healthEl.innerHTML =
      `<span class="hl">HP</span><span class="hv">${healthText(v)}</span>`;
  }

  // ---- noise meter -------------------------------------------------------
  // `state` is world.noiseState() — { n: metres audible, v: visibility mul }.
  // Only `n` is drawn; `v` is in the state object and still unread by anyone,
  // which is a second meter's worth of free information (see handoff notes).
  setNoise(state) {
    this._noiseFed = true;
    if (this._bridge) { clearInterval(this._bridge); this._bridge = null; }
    this._applyNoise(state);
  }

  _applyNoise(state) {
    const m = noiseMeter(state);
    const key = m.on ? `${m.bars}|${m.tone}|${m.n}` : 'off';
    if (key === this._noiseKey) return;
    this._noiseKey = key;
    if (!this._noiseSegs) {
      // built once and then mutated — the vitals column's lesson: rewriting
      // innerHTML on a readout that changes several times a second re-parses
      // the whole thing for one class flip
      const segs = document.createElement('span');
      segs.className = 'nsegs';
      this._noiseSegs = [];
      for (let i = 0; i < 5; i++) {
        const s = document.createElement('i');
        s.className = 'nsg';
        segs.appendChild(s);
        this._noiseSegs.push(s);
      }
      this._noiseNum = document.createElement('span');
      this._noiseNum.className = 'nm';
      this.noiseEl.appendChild(segs);
      this.noiseEl.appendChild(this._noiseNum);
    }
    this.noiseEl.className = m.on ? `on ${m.tone}` : '';
    if (!m.on) return;
    for (let i = 0; i < this._noiseSegs.length; i++) {
      this._noiseSegs[i].classList.toggle('lit', i < m.bars);
    }
    this._noiseNum.textContent = `${m.n} M`;
  }

  // TEMPORARY BRIDGE — delete once main.js calls hud.setNoise().
  // The noise state lives on `world`, which the HUD has no reference to, and
  // this workstream does not own main.js. main.js already publishes `world`
  // on window.__game (its own harness hook), so the meter polls that at 8 Hz
  // until the one-line call in the vitals tick exists; the first real
  // setNoise() cancels the poll for good. The exact diff is in the notes.
  _bridgeNoise() {
    this._bridge = setInterval(() => {
      if (this._noiseFed) { clearInterval(this._bridge); this._bridge = null; return; }
      const w = typeof window !== 'undefined' ? window.__game?.world : null;
      if (w?.noiseState) this._applyNoise(w.noiseState());
    }, 125);
  }

  // ---- wound infection (docs/INFECTED.md Z54, Z125–Z131) ------------------
  // `stats` is src/game/stats.js. Deliberately quiet:
  //   • INCUBATION SHOWS NOTHING. Z125 makes the first 5–10 minutes silent —
  //     no symptoms, no damage, one line of toast when the wound is seeded —
  //     and a corner icon would give away exactly what the design withholds.
  //   • The plate appears with the first symptom, colour-steps the way the
  //     vitals do (gold → amber → red), fills its bar with severity and blinks
  //     only when it is critical, i.e. sepsis.
  //   • Painkillers quiet the symptoms, so they quiet the readout too: the
  //     glyph goes dim and stops pulsing while the damage carries on.
  //   • The stage is named in a label above the plate (NOT a title= — pointer
  //     lock means nothing in this game can ever be hovered), and that label
  //     settles away with the rest of the HUD. See the #hud-infection .ilabel
  //     comment in the stylesheet.
  setInfection(stats) {
    // the vitals tick is the only 4 Hz call that already carries `stats`, so
    // the numeric health readout rides along rather than asking main.js for a
    // second call site in a file this workstream does not own
    this.setHealth(stats);
    const inf = stats?.infection;
    const sev = Math.max(0, Math.min(1, stats?.infectionSeverity || 0));
    const symptomatic = !!inf && inf.stage && inf.stage !== 'incubating';
    const quiet = !!inf && inf.quietUntil > performance.now();
    const key = !symptomatic ? 'off' : `${inf.stage}|${quiet ? 'q' : ''}|${Math.round(sev * 20)}`;
    if (key === this._infKey) return;
    this._infKey = key;
    this.infEl.classList.toggle('on', symptomatic);
    // the health number shares the bottom-right line with this plate, so it
    // steps one slot left while the plate is up (both elements are ours)
    this.healthEl.classList.toggle('shift', symptomatic);
    if (!symptomatic) {
      this.infEl.innerHTML = '';
      this.feverVig.style.opacity = 0;
      this.feverVig.classList.remove('on');
      return;
    }
    const col = inf.stage === 'critical' ? 'var(--hud-crit)'
      : inf.stage === 'sick' ? 'var(--hud-bad)' : 'var(--hud-warn)';
    const pulse = quiet ? '' : inf.stage === 'critical' ? ' crit' : ' fever';
    const pct = Math.max(4, Math.round(sev * 100));
    // the label is escaped through textContent below, never interpolated
    const label = stats.infectionLabel?.() || 'Infected';
    this.infEl.innerHTML =
      '<div class="islot">' +
      `<svg class="iic${pulse}" viewBox="0 0 24 24" style="color:${col};opacity:${quiet ? 0.5 : 1}">` +
      // a clinical thermometer: fever reads instantly and cannot be confused
      // with the blood bag or the water droplet next to it
      '<g fill="currentColor"><path d="M10.4 2.6h3.2c1 0 1.8.8 1.8 1.8v9.1a5.2 5.2 0 1 1-6.8 0V4.4' +
      'c0-1 .8-1.8 1.8-1.8zm1.6 1.9c-.6 0-1 .4-1 1v9.8l-.5.4a3.3 3.3 0 1 0 3 0l-.5-.4V5.5c0-.6-.4-1-1-1z"/>' +
      '<path d="M11.1 7.4h1.8v9.9h-1.8z"/><circle cx="12" cy="18.4" r="2.4"/>' +
      '<path d="M16.2 5.6h3.2v1.5h-3.2zM16.2 8.7h2.2v1.5h-2.2zM16.2 11.8h3.2v1.5h-3.2z"/></g></svg>' +
      `<i class="iv" style="width:calc(${pct}% - 2px);background:${col};opacity:.9"></i></div>`;
    const lab = document.createElement('div');
    lab.className = 'ilabel';
    lab.style.color = inf.stage === 'critical' ? col : 'var(--hud-dim)';
    lab.textContent = quiet ? `${label} · quieted` : label;
    this.infEl.appendChild(lab);
    // the fever haze only arrives with the shakes, and painkillers dull it
    const haze = sev < 0.3 ? 0 : Math.min(0.16, (sev - 0.3) * 0.22) * (quiet ? 0.4 : 1);
    this.feverVig.style.opacity = haze;
    this.feverVig.classList.toggle('on', haze > 0);
  }

  toggleHelp() {
    this.help.style.display = this.help.style.display === 'block' ? 'none' : 'block';
  }

  // Gameplay messages go to the bottom-left toast queue (DayZ pattern).
  // `opts` is optional and every existing caller omits it: 'warn' | 'crit',
  // or { sev, key } to group rows that dedupe across different wordings.
  // Without it toast.js infers the severity from the text (see hudmodel.mjs).
  flashMove(label, opts) {
    this.toasts.push(label, opts);
  }

  hitmarker() {
    // retrigger the tick animation cleanly
    this.hitEl.classList.remove('go');
    void this.hitEl.offsetWidth;
    this.hitEl.classList.add('go');
    clearTimeout(this._hitTimer);
    this._hitTimer = setTimeout(() => this.hitEl.classList.remove('go'), 140);
  }

  setState(s) {
    if (this._debug) {
      this.state.innerHTML =
        `<b>stance</b> ${s.stance}${s.airborne ? ' (air)' : ''}  <b>mode</b> ${s.mode}<br>` +
        `<b>lean</b> ${s.lean < 0 ? 'left' : s.lean > 0 ? 'right' : '—'}  ` +
        `<b>block</b> ${s.block ? 'YES' : '—'}  <b>focus</b> ${(s.focus * 100) | 0}%<br>` +
        `<b>anim</b> ${s.anim}<br>` +
        `<b>last move</b> ${s.lastMove}<br>` +
        `<b>pos</b> ${s.pos.x.toFixed(1)}, ${s.pos.z.toFixed(1)}`;
    }
    // ADS: the mark steps down, and the periphery falls away. No brackets.
    const ads = !!s.aiming;
    if (ads !== this._ads) {
      this._ads = ads;
      this.crosshair.classList.toggle('ads', ads);
      this.adsVig.classList.toggle('on', ads);
    }
    // weapon readout only when a gun is out — fists/melee show nothing (DayZ).
    // Rebuilt only when the string actually changes: this runs every frame, and
    // an innerHTML write per frame would restart the reload sweep animation.
    const key = s.ammo ? `${s.weapon}|${s.ammo}|${s.reloading ? 1 : 0}` : '';
    if (key !== this._wKey) {
      // a magazine that changed is news — wake the HUD for it (firing, reload,
      // weapon swap), which is exactly when a player wants the count back
      if (this._wKey !== undefined) pokeHud();
      this._wKey = key;
      if (s.ammo) {
        const sweep = s.reloading
          ? '<svg class="sweep" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" ' +
            'stroke="rgba(255,255,255,.25)" stroke-width="2"/><path d="M8 2a6 6 0 0 1 6 6" ' +
            'stroke="#e6e6e6" stroke-width="2" stroke-linecap="round"/></svg>'
          : '';
        const [mag, cap] = String(s.ammo).split('/');
        this.weaponEl.innerHTML =
          `<div class="wname">${s.weapon}</div>` +
          `<span class="ammo"><b>${mag}</b><i>/${cap}</i></span>${sweep}`;
      } else {
        this.weaponEl.innerHTML = '';
      }
    }
  }

  loading(msg) {
    this.move.textContent = msg;
    this.move.style.opacity = 0.92;
    clearTimeout(this._moveTimer);
    this._moveTimer = setTimeout(() => { this.move.style.opacity = 0; }, 900);
  }
}
